/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import MsDyn365, { IGridSettings, IImageData, Image, RichText, RichTextComponent } from '@msdyn365-commerce/core';
import { Button } from '@msdyn365-commerce-modules/utilities';
import { getDynamicImageSettings, getResponsiveImage } from '../../Utilities/get-dynamic-image';
import { IDynamicImagesData, ISuggestionCardLinkData } from './d5-mfrm-suggestion-cards.props.autogenerated';

import * as React from 'react';

interface ISuggestionCardData {
    image?: IImageData | undefined;
    cardIndex: number;
    heading: RichText | undefined;
    content: RichText | undefined;
    location?: RichText | undefined;
    gridSettings: IGridSettings;
    cardLink?: ISuggestionCardLinkData[] | undefined;
    isHorizontalCards: boolean | undefined;
    useDynamicImages: boolean | undefined;
    dynamicImages: IDynamicImagesData | undefined;
}

export default (props: ISuggestionCardData) => {
    const styles = 'mfrm-suggestion-cards__';
    const { image, location = '', heading, content, cardLink, isHorizontalCards, gridSettings, useDynamicImages, dynamicImages } = props;

    let currentImage;

    // Only use Responsive Image if Dynamic Images are defined
    if (dynamicImages && useDynamicImages) {
        currentImage = getResponsiveImage({
            desktop: dynamicImages?.desktopImage,
            tablet: dynamicImages?.tabletImage,
            mobile: dynamicImages?.mobileImage,
            defaultImg: image,
            imageSettingViewports: getDynamicImageSettings({ moduleName: 'd5-mfrm-suggestion-cards' })
        });
    }

    const handleAnalytics = (cl: ISuggestionCardLinkData) => {
        if (MsDyn365.isBrowser && cl) {
            const openLinkInNewTab = cl.openInNewTab ? '_blank' : '_self';
            if (cl.linkUrl.destinationUrl) {
                if (cl.linkText?.includes('Call Us')) {
                    // @ts-ignore
                    if ('utag' in window) {
                        // @ts-ignore
                        utag.link({
                            event: 'call-engagement-content-card',
                            event_type: 'call_engagement',
                            event_category: 'call',
                            event_action: 'click from Vertical Suggestion Card',
                            event_label: 'call engagement',
                            event_noninteraction: 'false'
                        });
                    }
                    const phoneNumber = document.querySelector('.ms-header__topbar__link.fw-700') as HTMLAnchorElement;
                    window.location.href = phoneNumber.href;
                } else if (cl.linkText?.includes('Chat Online')) {
                    // @ts-ignore
                    if ('utag' in window) {
                        // @ts-ignore
                        utag.link({
                            event: 'chat-widget-initiated-content-card',
                            event_type: 'chat_click',
                            event_category: 'chat',
                            event_action: 'click from Vertical Suggestion Card',
                            event_label: 'chat engagement',
                            event_noninteraction: 'false'
                        });
                    }
                    let c2c_counter = 0;
                    const c2c_check = setInterval(() => {
                        c2c_counter++;

                        // @ts-ignore // The 3rd party script may not have yet loaded and initialized
                        if (window.embedded_svc?.domInitInProgress === true) {
                            clearInterval(c2c_check);

                            // Trigger click event on live help button, minimized chat button, or sidebar minimized button
                            document
                                .querySelectorAll(
                                    '.uiButton.helpButtonEnabled, .minimizedContainer.helpButton, .sidebarHeader.minimizedContainer'
                                )
                                .forEach(_elem => {
                                    _elem.dispatchEvent(new Event('click'));
                                });
                        } else {
                            // Stop trying for the button after 5 seconds
                            c2c_counter > 10 && clearInterval(c2c_check);
                        }
                    }, 500);
                } else if (cl.linkText?.includes('Find A Store')) {
                    // @ts-ignore
                    if ('utag' in window) {
                        // @ts-ignore
                        utag.link({
                            event: 'store-locator-content-card',
                            event_type: 'Store Locator',
                            event_category: 'click from Store Locator Content Card',
                            event_action: 'click from Sleep Expert Pick Module',
                            event_label: 'D5 store locator engagement',
                            event_noninteraction: 'false'
                        });
                    }
                    window.open(cl.linkUrl.destinationUrl, openLinkInNewTab);
                } else if (cl.linkText?.includes('Find Your Match')) {
                    // @ts-ignore
                    if ('utag' in window) {
                        // @ts-ignore
                        utag.link({
                            event: 'mattress-matcher-content-card',
                            event_category: 'mattress matcher content card',
                            event_action: 'start button CTA click',
                            event_label: 'home page d5',
                            event_noninteraction: 'false'
                        });
                    }
                    window.open(cl.linkUrl.destinationUrl, openLinkInNewTab);
                } else if (cl.linkText?.includes('Learn More')) {
                    // @ts-ignore
                    if ('utag' in window) {
                        // @ts-ignore
                        utag.link({
                            event: 'sleepdotcom-app-content-card',
                            event_category: 'sleepdotcom app content card',
                            event_action: `CTA click to: ${cl.linkUrl.destinationUrl}`,
                            event_label: `from: ${window.location.href}`,
                            event_noninteraction: 'false'
                        });
                    }
                    window.open(cl.linkUrl.destinationUrl, openLinkInNewTab);
                } else {
                    window.open(cl.linkUrl.destinationUrl, openLinkInNewTab);
                }
            }
        }
    };

    const handleClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent> | React.MouseEvent<HTMLAnchorElement, MouseEvent>, cl: ISuggestionCardLinkData) => {
        event.preventDefault();
        handleAnalytics(cl);
    };

    const hasOneLinkOnly = cardLink?.length === 1;

    const suggestionCard = (
        <div className={`${styles}card${isHorizontalCards ? ' horizontal' : ''}`}>
            {useDynamicImages ? (
                <div className={`${styles}image-container${isHorizontalCards ? ' horizontal' : ''}`}>{currentImage}</div>
            ) : (
                <div className={`${styles}image-container${isHorizontalCards ? ' horizontal' : ''}`}>
                    <Image
                        {...image}
                        alt={image?.altText}
                        className={`${styles}image${isHorizontalCards ? ' horizontal' : ''}`}
                        src={image?.src || ''}
                        gridSettings={gridSettings}
                    />
                </div>
            )}
            <div className={`${styles}text-container${isHorizontalCards ? ' horizontal' : ''}`}>
                <div className={`${styles}text-container-content`}>
                    <div className={`${styles}card-heading`}>
                        <RichTextComponent text={heading} />
                    </div>
                    {content && (
                        <RichTextComponent className={`${styles}card-content${isHorizontalCards ? ' horizontal' : ''}`} text={content} />
                    )}
                    {location && (
                        <RichTextComponent
                            className={`${styles}card-location`}
                            text={`${location} <span class='mfrm-suggestion-cards__card-location-name'>Tom's River</span> 3.25 mi `}
                        />
                    )}
                </div>
                {cardLink && (
                    <div className={`${styles}buttoncontainer${isHorizontalCards ? ' horizontal' : ''}`}>
                        {hasOneLinkOnly ?
                            <Button
                                key={cardLink[0].linkText}
                                className={`d5-mfrm-single-cta${isHorizontalCards ? ' horizontal' : ''}`}
                                title={cardLink[0].linkText}
                            >
                                {cardLink[0].linkText}
                            </Button> :
                            <>{cardLink.map(cl => (
                                <Button
                                    key={cl.linkText}
                                    className={`d5-mfrm-single-cta${isHorizontalCards ? ' horizontal' : ''}`}
                                    title={cl.linkText}
                                    onClick={e => handleClick(e, cl)}
                                >
                                    {cl.linkText}
                                </Button>
                            ))}</>
                        }
                    </div>
                )}
            </div>
        </div>
    );

    const singleCardLink = cardLink![0].linkUrl.destinationUrl || '';

    return (
        <>
        {!hasOneLinkOnly ? (suggestionCard) : (
            <a className='mfrm-suggestion-cards__click-banner'
               // @ts-ignore
               onClick={(e) => handleClick(e, cardLink[0])}
               href={singleCardLink}
            >
                {suggestionCard}
            </a>
        )}
        </>
    );
};
